import { getNames } from "i18n-iso-countries"
import i18next from "i18next"
import { orderBy } from "lodash"
import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"

import { isEmptyOrBlank, listToRecord } from "@axtesys/react-tools"

import { sanitizeForSorting } from "./Transliteration"

export function useCountryNames() {
  const { i18n } = useTranslation()
  return getNames(i18n.language, { select: "official" })
}

export function useGetCountryName() {
  const countries = useCountryNames()

  return useCallback(
    (countryCode?: string) => {
      if (isEmptyOrBlank(countryCode)) return ""
      return countries[countryCode!]
    },
    [countries],
  )
}

export function useCountryCodes() {
  const countries = useCountryNames()

  return useMemo(() => {
    const orderedCountries = orderByString(
      Object.entries(countries).map(([countryCode, countryName]) => ({
        countryCode,
        countryName,
      })),
      ({ countryName }) => countryName,
    )

    return listToRecord(
      orderedCountries,
      ({ countryCode }) => countryCode,
      ({ countryName }) => countryName,
    )
  }, [countries])
}

export function getCountryName(countryCode?: string) {
  if (isEmptyOrBlank(countryCode)) return ""
  return getNames(i18next.language, { select: "official" })[countryCode!]
}

export function orderByString<T>(
  array: T[],
  iteratee: (item: T) => string | undefined,
) {
  return orderBy(array, item => sanitizeForSorting(iteratee(item)))
}

export function formatNumberToTwoDecimals(number: number) {
  const formatter = Intl.NumberFormat(i18next.language, {
    useGrouping: false,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

  return formatter.format(number)
}
